import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import ProductDetailPage from '@features/admin/products/ProductDetailPage.tsx';
import { useAdminProductDetailStore } from '@features/admin/products/useAdminProductDetailStore.ts';

const paramsSchema = z.object({
  productId: z.coerce.number().positive(),
});

const searchSchema = z.object({
  // Aktivní tab
  tab: z.enum(['auctions', 'suppliers', 'customers', 'prices']).optional().default('auctions'),
  // Stránkování
  page: z.coerce.number().optional().default(1),
  size: z.coerce.number().optional().default(20),
  // Filtry
  q: z.string().optional(),
  auctionId: z.coerce.number().optional(),
  supplierId: z.coerce.number().optional(),
  customerId: z.coerce.number().optional(),
  // Filtry pro ceny
  priceType: z.enum(['STANDARD', 'ACTION_PRICE', 'SUBSTITUTE_PRICE', 'FROM_DL_PRICE']).optional(),
  isGuaranteed: z.coerce.boolean().optional(),
});

export const Route = createFileRoute('/admin/products/$productId')({
  params: {
    parse: v => paramsSchema.parse(v),
  },
  validateSearch: search => searchSchema.parse(search),
  component: RouteComponent,
  beforeLoad: async ({ params, search }) => {
    const productId = paramsSchema.parse(params).productId;
    const store = useAdminProductDetailStore.getState();

    // Načti základní detail produktu
    await store.loadProductDetail(productId);

    // Načti všechny záložky paralelně - aktivní tab s parametry z URL, ostatní s výchozími hodnotami
    const { tab, page, size, q, auctionId, supplierId, customerId, priceType, isGuaranteed } = search;

    await Promise.all([
      store.loadImages(productId),
      store.loadAuctions(productId, tab === 'auctions' ? { page, size, q } : { page: 1, size: 20 }),
      store.loadSuppliers(productId, tab === 'suppliers' ? { page, size, q } : { page: 1, size: 20 }),
      store.loadCustomers(productId, tab === 'customers' ? { page, size, q } : { page: 1, size: 20 }),
      store.loadPrices(
        productId,
        tab === 'prices'
          ? {
              page,
              size,
              auctionId,
              supplierId,
              customerId,
              priceType,
              isGuaranteed,
            }
          : { page: 1, size: 20 },
      ),
    ]);
  },
});

function RouteComponent() {
  const { productId } = Route.useParams();
  return <ProductDetailPage productId={productId} />;
}
